// «اسأل المورد» — phase 1, staff-relayed (owner, 2026-10-30). A trader asks
// about a store product; staff relay the question to HioBuy (WhatsApp) and
// post the supplier's answer back. Tables and rules: migration
// market_inquiries (every message masked for contact details, both ways).
// Phase 2 (direct, with automatic translation) reuses all of this: only how a
// supplier message arrives changes (via 'direct'), never what the screens read.

import { marketDb as db, marketRpc as rpc } from './marketDb';

export const EXPECTED_REPLY = { ar: 'المورد يرد عادة خلال 24 ساعة', en: 'The supplier usually replies within 24 hours', zh: '供应商通常在24小时内回复' };

// ── trader ──────────────────────────────────────────────────────────────────
export const openInquiry = (productId, skuKey) => rpc('market_inquiry_open', { p_product_id: productId, p_sku_key: skuKey || null });
export const sendInquiry = (id, content) => rpc('market_inquiry_send', { p_inquiry_id: id, p_content: content });
export const markInquiryRead = (id) => rpc('market_inquiry_mark_read', { p_inquiry_id: id });
export const myInquiries = () => rpc('market_my_inquiries', {});
export async function fetchInquiry(id) {
  const { data, error } = await db.from('market_inquiries').select('id, product_id, sku_key, product_ref, status, mode, created_at, kind, order_id').eq('id', id).maybeSingle();
  return { inquiry: data || null, error };
}
// ThreadChat's message shape: sender_role is who speaks — a staff note renders as a centred system line.
export async function fetchInquiryMessages(id) {
  const { data, error } = await db.from('market_inquiry_messages').select('id, author, content, created_at').eq('inquiry_id', id).order('created_at');
  if (error) throw error;
  return (data || []).map(toChatMessage);
}
export const toChatMessage = (m) => ({ id: m.id, sender_role: m.author === 'staff' ? 'admin' : m.author, content: m.content, created_at: m.created_at });

// ── staff ───────────────────────────────────────────────────────────────────
export const adminInquiries = (status) => rpc('admin_market_inquiries', { p_status: status || null });
export const adminPostInquiry = (id, content, author = 'supplier') => rpc('admin_market_inquiry_post', { p_inquiry_id: id, p_content: content, p_author: author });
export const adminCloseInquiry = (id) => rpc('admin_market_inquiry_close', { p_inquiry_id: id });
// the supplier's answer gave a weight or size → the product's data (source 'supplier')
export const adminSaveSupplierMeasures = (row) => rpc('admin_market_supplier_measures', { p_rows: [row] });

// The WhatsApp text staff send HioBuy: the trader's waiting questions (in
// Chinese, translated by the caller), the 1688 link and the variant.
export function relayMessage({ questionsZh, sourceUrl, skuSpecId, title }) {
  return [
    '您好，客户想咨询这个产品：',
    title || null,
    sourceUrl || null,
    skuSpecId ? `规格 SKU ID: ${skuSpecId}` : null,
    '',
    ...questionsZh.map((q, i) => `${i + 1}. ${q}`),
    '',
    '如果方便，请同时告诉我们单件的净重、包装后重量和包装尺寸（长×宽×高 cm）。谢谢！',
  ].filter((x) => x !== null).join('\n');
}

// The trader questions still waiting for an answer: everything after the last supplier message.
export function waitingQuestions(messages) {
  const out = [];
  for (const m of messages) {
    if (m.sender_role === 'supplier') out.length = 0;
    else if (m.sender_role === 'trader') out.push(m.content);
  }
  return out;
}

// ── where the supplier is: 1688's region (广东省佛山市) in the reader's language ──
const PROVINCES = {
  广东: ['قوانغدونغ', 'Guangdong'], 浙江: ['تشجيانغ', 'Zhejiang'], 江苏: ['جيانغسو', 'Jiangsu'], 山东: ['شاندونغ', 'Shandong'],
  福建: ['فوجيان', 'Fujian'], 河北: ['خبي', 'Hebei'], 河南: ['خنان', 'Henan'], 上海: ['شنغهاي', 'Shanghai'], 北京: ['بكين', 'Beijing'],
  天津: ['تيانجين', 'Tianjin'], 重庆: ['تشونغتشينغ', 'Chongqing'], 安徽: ['آنهوي', 'Anhui'], 湖北: ['هوبي', 'Hubei'], 湖南: ['هونان', 'Hunan'],
  江西: ['جيانغشي', 'Jiangxi'], 四川: ['سيتشوان', 'Sichuan'], 辽宁: ['لياونينغ', 'Liaoning'], 吉林: ['جيلين', 'Jilin'],
  黑龙江: ['هيلونغجيانغ', 'Heilongjiang'], 陕西: ['شنشي', 'Shaanxi'], 山西: ['شانشي', 'Shanxi'], 广西: ['قوانغشي', 'Guangxi'],
  云南: ['يوننان', 'Yunnan'], 贵州: ['قويتشو', 'Guizhou'], 甘肃: ['قانسو', 'Gansu'], 内蒙古: ['منغوليا الداخلية', 'Inner Mongolia'],
  新疆: ['شينجيانغ', 'Xinjiang'], 海南: ['هاينان', 'Hainan'], 宁夏: ['نينغشيا', 'Ningxia'], 青海: ['تشينغهاي', 'Qinghai'],
  西藏: ['التبت', 'Tibet'], 香港: ['هونغ كونغ', 'Hong Kong'], 台湾: ['تايوان', 'Taiwan'],
};
const CITIES = {
  深圳: ['شنتشن', 'Shenzhen'], 广州: ['قوانغتشو', 'Guangzhou'], 东莞: ['دونغقوان', 'Dongguan'], 佛山: ['فوشان', 'Foshan'],
  汕头: ['شانتو', 'Shantou'], 揭阳: ['جيه يانغ', 'Jieyang'], 中山: ['تشونغشان', 'Zhongshan'], 惠州: ['هويتشو', 'Huizhou'],
  江门: ['جيانغمن', 'Jiangmen'], 潮州: ['تشاوتشو', 'Chaozhou'], 珠海: ['تشوهاي', 'Zhuhai'], 肇庆: ['تشاوتشينغ', 'Zhaoqing'],
  金华: ['جينهوا', 'Jinhua'], 温州: ['ونتشو', 'Wenzhou'], 宁波: ['نينغبو', 'Ningbo'], 台州: ['تايتشو', 'Taizhou'],
  杭州: ['هانغتشو', 'Hangzhou'], 绍兴: ['شاوشينغ', 'Shaoxing'], 嘉兴: ['جياشينغ', 'Jiaxing'], 湖州: ['هوتشو', 'Huzhou'],
  义乌: ['ييوو', 'Yiwu'], 苏州: ['سوتشو', 'Suzhou'], 无锡: ['ووشي', 'Wuxi'], 常州: ['تشانغتشو', 'Changzhou'],
  南通: ['نانتونغ', 'Nantong'], 南京: ['نانجينغ', 'Nanjing'], 扬州: ['يانغتشو', 'Yangzhou'], 临沂: ['لينيي', 'Linyi'],
  青岛: ['تشينغداو', 'Qingdao'], 济南: ['جينان', 'Jinan'], 潍坊: ['ويفانغ', 'Weifang'], 烟台: ['يانتاي', 'Yantai'],
  泉州: ['تشيوانتشو', 'Quanzhou'], 厦门: ['شيامن', 'Xiamen'], 福州: ['فوتشو', 'Fuzhou'], 莆田: ['بوتيان', 'Putian'],
  漳州: ['تشانغتشو', 'Zhangzhou'], 保定: ['باودينغ', 'Baoding'], 沧州: ['تسانغتشو', 'Cangzhou'], 石家庄: ['شيجياتشوانغ', 'Shijiazhuang'],
  邢台: ['شينغتاي', 'Xingtai'], 郑州: ['تشنغتشو', 'Zhengzhou'], 武汉: ['ووهان', 'Wuhan'], 长沙: ['تشانغشا', 'Changsha'],
  成都: ['تشنغدو', 'Chengdu'], 合肥: ['خفي', 'Hefei'],
};
const MUNICIPAL = ['上海', '北京', '天津', '重庆'];

/** '广东省佛山市' → 'فوشان، قوانغدونغ' (ar) / 'Foshan, Guangdong' (en) / the original (zh). Unknown city → the province alone; unknown province → null. */
export function supplierCity(region, lang = 'ar') {
  const r = String(region || '').trim();
  if (!r) return null;
  if (lang === 'zh') return r;
  const i = lang === 'ar' ? 0 : 1;
  const prov = Object.keys(PROVINCES).sort((a, b) => b.length - a.length).find((p) => r.startsWith(p));
  if (!prov) return null;
  const rest = r.slice(prov.length).replace(/^(省|市|壮族自治区|回族自治区|维吾尔自治区|自治区|特别行政区)/, '');
  const city = Object.keys(CITIES).find((c) => rest.startsWith(c));
  const provName = PROVINCES[prov][i];
  if (MUNICIPAL.includes(prov)) return provName;
  if (!city) return provName;
  return `${CITIES[city][i]}${lang === 'ar' ? '، ' : ', '}${provName}`;
}
